import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import {
  ErrorEvent,
  ErrorEventSummary,
  ErrorFrame,
  ErrorIssueDetail,
  ErrorIssueStatus,
} from 'src/app/models/error-tracking.model';
import { ErrorTrackingService } from 'src/app/services/error-tracking.service';
import { ToastService } from 'src/app/services/toast.service';
import { TracingService } from 'src/app/services/tracing.service';

@Component({
  selector: 'app-project-error-detail',
  templateUrl: './project-error-detail.component.html',
  styleUrls: ['./project-error-detail.component.scss'],
  standalone: false
})
export class ProjectErrorDetailPageComponent implements OnInit {

  projectId: string = null;
  issueId: string = null;

  issue: ErrorIssueDetail | null = null;
  event: ErrorEvent | null = null;
  events: ErrorEventSummary[] = [];
  eventCount = 0;

  loading = true;
  error = false;
  updating = false;

  showAllFrames = false;

  /** Set when the trace this error belongs to was sampled and is still stored. */
  traceTransactionId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private errorTrackingService: ErrorTrackingService,
    private tracingService: TracingService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    this.projectId = this.route.snapshot.paramMap.get('uuid');
    this.issueId = this.route.snapshot.paramMap.get('issueUuid');

    try {
      const [issue, events] = await Promise.all([
        lastValueFrom(this.errorTrackingService.getIssue(this.projectId, this.issueId)),
        lastValueFrom(this.errorTrackingService.getIssueEvents(this.projectId, this.issueId)),
      ]);
      this.issue = issue;
      this.event = issue.latest_event;
      this.events = events.results;
      this.eventCount = events.count;
      await this.findTrace();
    } catch {
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  /** Traces expire before errors do, so a missing one is normal, not an error. */
  private async findTrace() {
    if (!this.event?.trace_id) {
      this.traceTransactionId = null;
      return;
    }

    try {
      const transactions = await lastValueFrom(
        this.tracingService.getTrace(this.projectId, this.event.trace_id)
      );
      this.traceTransactionId = transactions.length ? transactions[0].uuid : null;
    } catch {
      this.traceTransactionId = null;
    }
  }

  async selectEvent(summary: ErrorEventSummary) {
    if (this.event?.uuid === summary.uuid) {
      return;
    }
    try {
      this.event = await lastValueFrom(
        this.errorTrackingService.getEvent(this.projectId, this.issueId, summary.uuid)
      );
      await this.findTrace();
    } catch {
      this.toastService.show('Could not load that event.', 'error');
    } finally {
      this.cdr.markForCheck();
    }
  }

  async setStatus(status: ErrorIssueStatus) {
    this.updating = true;
    this.cdr.markForCheck();

    try {
      const updated = await lastValueFrom(
        this.errorTrackingService.setIssueStatus(this.projectId, this.issueId, status)
      );
      this.issue = { ...updated, latest_event: this.issue?.latest_event ?? null };
      this.toastService.show(`Issue marked as ${status}.`);
    } catch {
      this.toastService.show('Could not update the issue.', 'error');
    } finally {
      this.updating = false;
      this.cdr.markForCheck();
    }
  }

  async deleteIssue() {
    if (!confirm('Delete this issue and all of its events? This cannot be undone.')) {
      return;
    }

    try {
      await lastValueFrom(this.errorTrackingService.deleteIssue(this.projectId, this.issueId));
      this.toastService.show('Issue deleted.');
      this.goBack();
    } catch {
      this.toastService.show('Could not delete the issue.', 'error');
    }
  }

  goBack() {
    this.router.navigate(['/main', 'project', this.projectId, 'errors']);
  }

  // -- stack trace --------------------------------------------------------

  get frames(): ErrorFrame[] {
    const all = this.event?.stacktrace ?? [];
    // Deepest frame first: that is where the error was actually thrown.
    const ordered = [...all].reverse();
    if (this.showAllFrames) {
      return ordered;
    }
    const inApp = ordered.filter(f => f.in_app);
    return inApp.length ? inApp : ordered;
  }

  get hiddenFrameCount(): number {
    const all = this.event?.stacktrace?.length ?? 0;
    return Math.max(all - this.frames.length, 0);
  }

  frameLocation(frame: ErrorFrame): string {
    const position = [frame.lineno, frame.colno].filter(v => v !== null).join(':');
    return position ? `${frame.filename} in ${frame.function} at line ${position}` : frame.filename;
  }

  contextLines(frame: ErrorFrame): { lineno: number | null; text: string; isError: boolean }[] {
    const lines: { lineno: number | null; text: string; isError: boolean }[] = [];
    const start = frame.lineno ? frame.lineno - (frame.pre_context?.length ?? 0) : null;

    (frame.pre_context ?? []).forEach((text, i) => {
      lines.push({ lineno: start !== null ? start + i : null, text, isError: false });
    });
    if (frame.context_line) {
      lines.push({ lineno: frame.lineno, text: frame.context_line, isError: true });
    }
    (frame.post_context ?? []).forEach((text, i) => {
      lines.push({ lineno: frame.lineno ? frame.lineno + i + 1 : null, text, isError: false });
    });

    return lines;
  }

  // -- formatting ---------------------------------------------------------

  get tagEntries(): { key: string; value: string }[] {
    return Object.entries(this.event?.tags ?? {}).map(([key, value]) => ({ key, value }));
  }

  get extraEntries(): { key: string; value: string }[] {
    return Object.entries(this.event?.extra ?? {}).map(([key, value]) => ({ key, value }));
  }

  get userEntries(): { key: string; value: string }[] {
    return Object.entries(this.event?.user_context ?? {}).map(([key, value]) => ({ key, value }));
  }

  get headerEntries(): { key: string; value: string }[] {
    return Object.entries(this.event?.request?.headers ?? {}).map(([key, value]) => ({ key, value }));
  }

  get requestBody(): string {
    const data = this.event?.request?.data;
    if (data === null || data === undefined || data === '') {
      return '';
    }
    return typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  }

  /**
   * True when the app never said who the user was: then "0 users affected" would be
   * misleading, so the page explains how to send the user instead.
   */
  get noUserReported(): boolean {
    const context = this.issue?.latest_event?.user_context ?? {};
    return !!this.issue && !this.issue.users_seen && Object.keys(context).length === 0;
  }

  /** Where to read how to attach the user, for the SDK that reported this issue. */
  get userSetupGuide(): { link: string; label: string } {
    if (this.issue?.platform === 'django') {
      return { link: '/knowledge-base/sdks-api/django-sdk', label: 'See how the user is reported' };
    }
    if (this.issue?.platform === 'angular') {
      return { link: '/knowledge-base/sdks-api/angular-sdk', label: 'Call setUser() after login' };
    }
    return { link: '/knowledge-base/sdks-api/report-errors-over-http', label: 'Send a user with each event' };
  }

  relativeTime(value: string): string {
    const diffSeconds = (Date.now() - new Date(value).getTime()) / 1000;

    if (diffSeconds < 60) return 'just now';
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    if (diffSeconds < 2592000) return `${Math.floor(diffSeconds / 86400)}d ago`;
    return new Date(value).toLocaleDateString();
  }

  formatDateTime(value: string): string {
    return new Date(value).toLocaleString();
  }

  // Breadcrumbs of one event are milliseconds apart, so the local time with ms is what tells them apart.
  crumbTime(value: string): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  }
}
